// qc-board/permissions.ts — cổng trang Chất lượng nhân sự. Mirror BE: action
// review.read (Đợt 1 cấp cho QC + trưởng cửa hàng); trang còn bị ma trận
// page-access khoá theo vai (qc_board: admin/director/qc) — cổng này là lớp hai.
import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission, usePermission } from "@/lib/authv2/permissions";

export function canViewQcBoard(staff: Pick<Staff, "id" | "role"> | null | undefined): boolean {
  return !!staff && hasPermission(ACTION.ReviewRead);
}

/**
 * Ai được đặt GIÁ ba mốc phạt lỗi (quyết định 0074). Mirror BE action
 * `incident.penalty.config.write` = admin/director. CỐ Ý không có `qc`:
 * người ghi lỗi không được tự định giá lỗi mình ghi. Bản phản ứng (hook) vì
 * ô này nằm giữa trang — phải hiện/ẩn lại khi phiên đăng nhập đổi.
 */
export function useCanEditPenaltyConfig(): boolean {
  return usePermission(ACTION.IncidentPenaltyConfigWrite);
}

/**
 * Ai được thấy khối ĐIỂM KPI + TIỀN (phần trăm quỹ thưởng, tiền phạt) trong
 * ngăn chi tiết. CEO chốt 01/09/2026 (đáp án 4b): giám đốc VÀ trưởng cửa hàng
 * thấy — để còn giải thích được cho nhân viên; QC KHÔNG thấy.
 *
 * Mirror BE `performance.read.team` = admin/director/branch_manager/
 * area_manager — đúng bằng danh sách CEO chốt, nên dùng thẳng, không tự dựng
 * danh sách vai riêng ở màn (danh sách vai chép tay là chỗ lệch với máy chủ).
 */
export function useCanSeeKpiAndMoney(): boolean {
  return usePermission(ACTION.PerformanceReadTeam);
}

/** Ai được xem hồ sơ nghề (lịch sử điểm từng tháng, chuỗi ngày sạch lỗi). */
export function useCanSeeDossier(): boolean {
  return usePermission(ACTION.PerformanceDossierRead);
}

/** Ai được xem sổ công việc (số bộ ảnh đã làm) — KHÔNG có QC, KHÔNG có trưởng cửa hàng. */
export function useCanSeeWorkLedger(): boolean {
  return usePermission(ACTION.WorkLedgerRead);
}

/**
 * Ba nghề trưởng cửa hàng ghi lỗi được (quyết định 0134): lễ tân, thợ ảnh —
 * `photo` là TIỀN TỐ nên phủ cả bốn mã phòng ảnh đang có trong hồ sơ nhân sự
 * (`photo` gộp, `photo_idphoto`, `photo_profile`, `photo_corporate`) — và
 * trang điểm.
 *
 * Mirror `branchManagerSubjectGroups` ở `backend/internal/core/branchshift/
 * subject_dept.go`. Máy chủ là chốt chặn thật; danh sách này chỉ để màn hình
 * không mời người ta làm việc sẽ bị từ chối. Sửa một bên thì sửa cả hai —
 * `permissions.test.ts` ghim lại tập này.
 */
export const BRANCH_MANAGER_DEPTS = ["reception", "photo", "makeup"] as const;

/**
 * Bốn mã phòng ảnh trong hồ sơ nhân sự đếm là MỘT nghề. Gương của
 * `deptPartFor` bên `backend/internal/core/review/deptfilter.go` — sửa một bên
 * thì sửa cả hai.
 */
function gopNhomAnh(dept: string): string {
  return dept.startsWith("photo") ? "photo" : dept;
}

/**
 * Phạm vi PHÒNG BAN của bảng, suy từ vai + hồ sơ người đang xem.
 *
 * Ba câu trả lời, cố ý phân biệt hai câu cuối:
 *   - `null`   → không khoá, người xem tự chọn phòng ở ô lọc (giám đốc, admin).
 *   - `[...]`  → khoá cứng vào tập này, ô lọc phòng KHÔNG hiện.
 *   - `[]`     → khoá nhưng KHÔNG biết khoá vào đâu (QC chưa gán phòng).
 *                Trang tự khoá kín và chỉ đường đi gán phòng, không hiện số
 *                của ai — fail-closed, giống `DeptFilterFor` ở máy chủ.
 *
 * Tham số thứ ba là danh sách phòng giám đốc gán tay (migration 0331). RỖNG
 * hoặc vắng mặt nghĩa là CHƯA KHAI: đọc tiếp ô `department` như trước, y hệt
 * `auth.DepartmentsOf` ở máy chủ. Hiểu rỗng thành "không phòng nào" là khoá
 * kín màn của mọi người QC đang làm việc.
 */
export function qcBoardDeptLock(
  role: string,
  department: string,
  qcDepartments?: readonly string[],
): string[] | null {
  const r = role.toLowerCase().trim();
  if (r === "qc") {
    const gan = (qcDepartments ?? [])
      .map((d) => d.toLowerCase().trim())
      .filter((d) => d !== "");
    if (gan.length > 0) return [...new Set(gan.map(gopNhomAnh))];
    const d = department.toLowerCase().trim();
    if (d === "") return [];
    return [gopNhomAnh(d)];
  }
  if (r === "branch_manager") return [...BRANCH_MANAGER_DEPTS];
  return null;
}

/**
 * ⚠️ KHÔNG có rào nghề thứ hai ở ngăn chi tiết, và đó là CỐ Ý.
 *
 * Form "Ghi lỗi cho …" nằm trong ngăn chi tiết, mà ngăn chỉ mở được từ một
 * dòng của bảng — bảng thì đã lọc bằng chính `qcBoardDeptLock` ở trên. Người
 * ngoài phạm vi không có dòng nào để bấm, nên một lần hỏi lại ở ngăn là mã
 * KHÔNG BAO GIỜ CHẠY: đã thử tháo nó ra, không bài kiểm nào đỏ. Mã không có
 * đường tới thì sẽ trôi khỏi luật thật mà không ai hay, nên thà không có.
 *
 * Chốt chặn thật cho việc ghi lỗi nằm ở MÁY CHỦ — `checkSubjectDept` trong
 * `backend/internal/core/branchshift/subject_dept.go`, áp cho mọi cửa
 * `POST /incidents` kể cả màn Ca vận hành. Nếu sau này ngăn chi tiết mở được
 * từ đường khác (tìm theo tên, mở thẳng bằng link…) thì lúc đó mới thêm rào ở
 * đây — và thêm kèm bài kiểm chứng minh nó có đường tới.
 */
